import { useState } from "react";
import {
    Box,
    Button,
    HStack,
    IconButton,
    Spinner,
    Table,
    Text,
    VStack,
} from "@chakra-ui/react";
import { HiArrowDownTray, HiEye, HiPaperClip } from "react-icons/hi2";
import dayjs from "dayjs";

const formatFileSize = (bytes) => {
    if (bytes == null) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const LeadAttachmentsTab = ({ attachments, isLoading, onOpenAttachment, onDownloadAttachment, onUploadAttachments, onDownloadAllZip }) => {
    const [isUploading, setIsUploading] = useState(false);

    const handleFilesSelected = async (e) => {
        const files = Array.from(e.target.files ?? []);
        e.target.value = "";
        if (!files.length) return;
        setIsUploading(true);
        try {
            await onUploadAttachments(files);
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Box>
            <HStack justify="flex-end" mb={4}>
                <Button variant="outlined" size="sm" disabled={attachments.length === 0} onClick={onDownloadAllZip}>
                    Pobierz wszystko (ZIP)
                </Button>
                <Button
                    as="label"
                    htmlFor="lead-attachment-file-input"
                    variant="outlined"
                    size="sm"
                    disabled={isLoading || isUploading}
                    cursor="pointer"
                >
                    {isUploading ? <Spinner size="xs" /> : <HiPaperClip />}
                    Dodaj załącznik
                    <input
                        id="lead-attachment-file-input"
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        onChange={handleFilesSelected}
                    />
                </Button>
            </HStack>
            {isLoading ? (
                <VStack py={8} align="center"><Spinner color="purple.400" size="sm" /></VStack>
            ) : attachments.length === 0 ? (
                <VStack py={10} align="center">
                    <Text color="gray.500" fontSize="sm">Brak załączników</Text>
                </VStack>
            ) : (
                <>
                    <Box display={{ base: 'none', md: 'block' }} bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} overflowX="auto">
                        <Table.Root minW="720px">
                            <Table.Header>
                                <Table.Row bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
                                    {[
                                        "Nazwa pliku",
                                        "Typ",
                                        "Autor",
                                        "Data",
                                        "Rozmiar",
                                        "",
                                    ].map((col, i) => (
                                        <Table.ColumnHeader
                                            key={i}
                                            color="gray.500"
                                            fontWeight={600}
                                            fontSize="xs"
                                            textTransform="uppercase"
                                            letterSpacing="wider"
                                            py={3}
                                            px={5}
                                            border="none"
                                            textAlign={i === 5 ? "right" : "left"}
                                        >
                                            {col}
                                        </Table.ColumnHeader>
                                    ))}
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {attachments.map((att) => (
                                    <Table.Row
                                        key={att.name}
                                        bg="white"
                                        borderBottom="1px solid"
                                        borderColor="gray.200"
                                        _hover={{ bg: "gray.50" }}
                                        transition="background 0.15s"
                                    >
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="gray.900" fontSize="sm" fontFamily="mono">{att.name}</Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="gray.500" fontSize="xs">{att.kind ?? att.type ?? "-"}</Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="gray.500" fontSize="xs">{att.authorFullName ?? "-"}</Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="gray.500" fontSize="xs">
                                                {att.createdAt ? dayjs(att.createdAt).format("DD.MM.YYYY HH:mm") : "-"}
                                            </Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none">
                                            <Text color="gray.500" fontSize="xs">{formatFileSize(att.fileSize)}</Text>
                                        </Table.Cell>
                                        <Table.Cell py={4} px={5} border="none" textAlign="right">
                                            {att.path && (
                                                <HStack justify="flex-end" gap={2}>
                                                    <IconButton
                                                        variant="outlined"
                                                        size="sm"
                                                        aria-label="Podgląd pliku"
                                                        onClick={() => onOpenAttachment(att)}
                                                    >
                                                        <HiEye />
                                                    </IconButton>
                                                    <IconButton
                                                        variant="outlined"
                                                        size="sm"
                                                        aria-label="Pobierz plik"
                                                        onClick={() => onDownloadAttachment(att)}
                                                    >
                                                        <HiArrowDownTray />
                                                    </IconButton>
                                                </HStack>
                                            )}
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table.Root>
                    </Box>
                    <VStack display={{ base: 'flex', md: 'none' }} gap={0} bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} overflow="hidden">
                        {attachments.map((att, i) => (
                            <HStack
                                key={att.name}
                                w="full"
                                justify="space-between"
                                px={4}
                                py={3}
                                borderBottom={i < attachments.length - 1 ? "1px solid" : "none"}
                                borderColor="gray.200"
                            >
                                <VStack align="flex-start" gap={0} flex={1} minW={0}>
                                    <Text color="gray.900" fontSize="sm" fontFamily="mono">{att.name}</Text>
                                    <Text color="gray.500" fontSize="xs">{att.kind ?? att.type ?? "-"}</Text>
                                    {att.authorFullName && (
                                        <Text color="gray.500" fontSize="xs">{att.authorFullName}</Text>
                                    )}
                                </VStack>
                                {att.path && (
                                    <HStack gap={2} flexShrink={0}>
                                        <IconButton
                                            variant="outlined"
                                            size="sm"
                                            aria-label="Podgląd pliku"
                                            onClick={() => onOpenAttachment(att)}
                                        >
                                            <HiEye />
                                        </IconButton>
                                        <IconButton
                                            variant="outlined"
                                            size="sm"
                                            aria-label="Pobierz plik"
                                            onClick={() => onDownloadAttachment(att)}
                                        >
                                            <HiArrowDownTray />
                                        </IconButton>
                                    </HStack>
                                )}
                            </HStack>
                        ))}
                    </VStack>
                </>
            )}
        </Box>
    );
};

export default LeadAttachmentsTab;
